import { CommonModule } from '@angular/common';
import { Component, Input, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import {
  AcademicAppointment,
  displayTime,
  sessionEnd,
  sessionStart,
} from '../../../../core/interfaces/academic-appointment';
import { SessionViewService } from '../../../../core/services/academic-appointments/session-view.service';
import { SessionJoinService } from '../../../../core/services/academic-appointments/session-join.service';

import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
/**
 * Liste des prochaines séances, partagée par les tableaux de bord professeur
 * et élève. Présentationnelle : l'appelant fournit les séances déjà chargées.
 */
@Component({
  selector: 'app-upcoming-sessions',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterLink, LocalizedDatePipe],
  templateUrl: './upcoming-sessions.component.html',
  styleUrl: './upcoming-sessions.component.css',
})
export class UpcomingSessionsComponent {
  readonly view = inject(SessionViewService);
  private readonly joinService = inject(SessionJoinService);

  /** Séances déjà chargées, tous horizons confondus. */
  @Input({ required: true }) set sessions(value: AcademicAppointment[]) {
    this.all.set(value ?? []);
  }

  @Input() loading = false;

  /** Nombre maximum de séances affichées. */
  @Input() limit = 5;

  /** Lien « voir tout » — diffère entre prof et élève. */
  @Input() calendarLink = '/student/agenda';

  /** Affiche la carte dans sa version compacte, sans cadre ni titre. */
  @Input() bare = false;

  private readonly all = signal<AcademicAppointment[]>([]);

  /**
   * Les séances déjà terminées sortent de la liste, celle en cours y reste
   * en tête : « prochaines » inclut ce qui se joue maintenant.
   */
  readonly upcoming = computed(() => {
    const now = Date.now();
    return this.all()
      .filter((s) => new Date(sessionEnd(s)).getTime() >= now)
      .sort((a, b) => sessionStart(a).localeCompare(sessionStart(b)))
      .slice(0, this.limit);
  });

  time(value: string | null | undefined): string {
    return displayTime(value);
  }

  start(s: AcademicAppointment): string {
    return sessionStart(s);
  }

  /** Une séance dont l'heure de début est passée mais pas la fin est en cours. */
  isLive(s: AcademicAppointment): boolean {
    const now = Date.now();
    return new Date(sessionStart(s)).getTime() <= now
        && new Date(sessionEnd(s)).getTime() >= now;
  }

  /** Le jour même, la date complète est du bruit : on n'affiche que l'heure. */
  isToday(s: AcademicAppointment): boolean {
    return (s.scheduledDate ?? '').slice(0, 10) === new Date().toISOString().slice(0, 10);
  }

  /** La réunion s'ouvre au clic ; l'accès est journalisé côté serveur. */
  join(s: AcademicAppointment): void {
    this.joinService.openMeeting(s.id, (err) =>
      console.warn('Join session failed:', err),
      s.meetingUrl,
    );
  }
}
